// useFinalQuizAbortGuard.ts
import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '@hooks/reduxHooks.ts';
import { setToggleTimerInFinalQuiz } from '@routes/sidebar/learning/___pages___/learning-overview/model/finalQuiz.slice.ts';
import { COMMON_QUIZ_STATUS } from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/quiz/___shared___/constants.ts';
import { openInNewTab } from '@utils/navigation.ts';

type AbortTarget = { kind: 'href'; href: string; newTab: boolean } | { kind: 'element'; el: HTMLElement } | null;

export interface UseFinalQuizAbortGuardOptions {
    quizStatus?: string;
    timeUpModalOpen: boolean;
}

const DISABLE_SELECTOR = '[data-quiz-disable="true"]';
export const DATA_FINAL_QUIZ_DISABLE = { 'data-quiz-disable': 'true' };

const isQuizStarted = (status?: string) => status === COMMON_QUIZ_STATUS.STARTED;

/**
 * Final-quiz abort guard:
 * - Default: clicks outside the quiz wrapper are allowed.
 * - Only elements with data-quiz-disable="true" (outside wrapper) trigger the ABORT modal.
 * - No beforeunload/refresh handling.
 */
export function useFinalQuizAbortGuard({ quizStatus, timeUpModalOpen }: UseFinalQuizAbortGuardOptions) {
    const dispatch = useAppDispatch();

    const [abortOpen, setAbortOpen] = useState(false);
    const pendingAbortRef = useRef<AbortTarget>(null);
    const abortedRef = useRef(false);
    const allowNextNavRef = useRef(false);
    const quizWrapperRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const onDocClickCapture = (e: MouseEvent) => {
            if (!isQuizStarted(quizStatus)) return;
            if (timeUpModalOpen || abortOpen) return;

            if (allowNextNavRef.current) {
                allowNextNavRef.current = false;
                return;
            }

            // only left click; let keyboard / context menu / other buttons pass
            if (e.button !== 0) return;

            const target = e.target as Element | null;
            if (!target) return;

            // Inside quiz wrapper → always allowed
            if (quizWrapperRef.current?.contains(target)) return;

            // Only block when explicitly disabled (app-level)
            const disabledMatch = target.closest(DISABLE_SELECTOR);
            if (!disabledMatch) return;

            const anchor = target.closest('a[href]') as HTMLAnchorElement | null;

            e.preventDefault();
            e.stopPropagation();

            setAbortOpen(true);
            abortedRef.current = false;

            if (anchor) {
                const newTabByModifier =
                    (e as MouseEvent).metaKey ||
                    (e as MouseEvent).ctrlKey ||
                    (e as MouseEvent).shiftKey ||
                    (e as MouseEvent).altKey ||
                    (e as MouseEvent).button === 1;

                pendingAbortRef.current = {
                    kind: 'href',
                    href: anchor.href,
                    newTab: anchor.target === '_blank' || newTabByModifier,
                };
            } else {
                const clickable =
                    (target.closest('button, [role="button"], [onclick]') as HTMLElement) ?? (target as HTMLElement);
                pendingAbortRef.current = { kind: 'element', el: clickable };
            }
        };

        document.addEventListener('click', onDocClickCapture, true);
        return () => document.removeEventListener('click', onDocClickCapture, true);
    }, [quizStatus, timeUpModalOpen, abortOpen, dispatch]);

    useEffect(() => {
        if (!isQuizStarted(quizStatus) || timeUpModalOpen) {
            dispatch(setToggleTimerInFinalQuiz(false));
        }

        // Modal precedence: Time's Up wins over ABORT
        if (timeUpModalOpen && abortOpen) {
            setAbortOpen(false);
            pendingAbortRef.current = null;
        }
    }, [quizStatus, timeUpModalOpen, abortOpen, dispatch]);

    const confirmAbortAndGo = () => {
        abortedRef.current = true;
        const action = pendingAbortRef.current;
        pendingAbortRef.current = null;
        if (!action) return;

        if (action.kind === 'href') {
            if (action.newTab) {
                openInNewTab(action.href);
            } else {
                window.location.href = action.href;
            }
        } else {
            allowNextNavRef.current = true;
            setTimeout(() => action.el.click(), 0);
        }
    };

    return {
        abortOpen,
        setAbortOpen,
        confirmAbortAndGo,
        quizWrapperRef,
        disableSelector: DISABLE_SELECTOR,
    };
}
